import { useQuery, useLazyQuery, QueryResult } from '@apollo/client';
import { OptionalString, parseQueryString, updateQueryString } from '@sixfold/app-utils';
import { Button, Heading, Select, Text } from '@sixfold/common-ui';
import { notNil } from '@sixfold/typed-primitives';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { apiEndpointUsageLogs, apiEndpointUsageLogsReportDownloadURIQuery } from '../../company/graphql';
import { InfiniteScroll } from '../../components/infinitescroll';
import {
  ApiEndpointUsageLogsQuery,
  ApiEndpointUsageLogsQueryVariables,
  ApiEndpointUsageLogsReportDownloadUriQuery,
  ApiEndpointUsageLogsReportDownloadUriQueryVariables,
  ApiHealth,
  getNodes,
  ImplementationStatus,
  loadMoreFromConnection,
} from '../../lib/graphql';
import { Loader, NetworkStatus } from '../../lib/loader';
import {
  ApiEndpointUsageLogInList,
  ApiEndpointUsageLogsList,
  getApiHealthLabel,
  getImplementationStatusLabel,
} from '../components/api_endpoint_usage_log_list';
import { EndpointDropdown } from '../components/endpoint_dropdown';

export interface ListProps {
  data: {
    company_id: string;
    company_name: string | null;
    api_endpoint_usage_logs: ApiEndpointUsageLogInList[];
  }[];
  result: QueryResult<ApiEndpointUsageLogsQuery, ApiEndpointUsageLogsQueryVariables>;
}

export const MAX_API_ENDPOINT_USAGE_LOGS = 500;

class LoadingContainer extends Loader<ApiEndpointUsageLogsQuery, ListProps, ApiEndpointUsageLogsQueryVariables> {}

const isValidImplementationStatus = (status: string): status is ImplementationStatus => {
  return Object.values(ImplementationStatus).includes(status as ImplementationStatus);
};

const isValidApiHealthStatus = (status: string): status is ApiHealth => {
  return Object.values(ApiHealth).includes(status as ApiHealth);
};

const getStatusKey = (status: ImplementationStatus | ''): string | null => {
  if (status === '') return null;
  return status;
};

const getApiHealthKey = (status: ApiHealth | ''): string | null => {
  if (status === '') return null;
  return status;
};

export const ApiUsageContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { endpoints, status, health } = useMemo(() => {
    const query = parseQueryString({
      queryString: location.search,
      queryParametersToParse: {
        endpoint: OptionalString,
        status: OptionalString,
        health: OptionalString,
      },
    });

    return {
      endpoints: notNil(query.endpoint) ? query.endpoint.split(',').filter((e) => e !== '') : [],
      status:
        notNil(query.status) && isValidImplementationStatus(query.status)
          ? (query.status as ImplementationStatus)
          : ('' as const),
      health:
        notNil(query.health) && isValidApiHealthStatus(query.health) ? (query.health as ApiHealth) : ('' as const),
    };
  }, [location.search]);

  const onSetEndpoints = (values: string[]) => {
    void navigate(
      `${location.pathname}?${updateQueryString(location.search, {
        param: 'endpoint',
        value: values.length > 0 ? values.join(',') : undefined,
      })}`,
      { replace: true },
    );
  };

  const onSetImplementationStatus = (value: string) => {
    const checkedValue = isValidImplementationStatus(value) ? (value as ImplementationStatus) : '';
    void navigate(
      `${location.pathname}?${updateQueryString(location.search, {
        param: 'status',
        value: checkedValue === '' ? undefined : checkedValue,
      })}`,
      { replace: true },
    );
  };

  const onSetApiHealth = (value: string) => {
    const checkedValue = isValidApiHealthStatus(value) ? (value as ApiHealth) : '';
    void navigate(
      `${location.pathname}?${updateQueryString(location.search, {
        param: 'health',
        value: checkedValue === '' ? undefined : checkedValue,
      })}`,
      { replace: true },
    );
  };

  const hasActiveFilters = endpoints.length > 0 || status !== '' || health !== '';

  const queryVariables = useMemo(
    (): ApiEndpointUsageLogsQueryVariables => ({
      first: MAX_API_ENDPOINT_USAGE_LOGS,
      endpoint: endpoints.length > 0 ? endpoints.join(',') : undefined,
      implementationStatus: status || undefined,
      apiHealth: health || undefined,
    }),
    [endpoints, status, health],
  );

  const result = useQuery<ApiEndpointUsageLogsQuery, ApiEndpointUsageLogsQueryVariables>(apiEndpointUsageLogs, {
    fetchPolicy: 'cache-and-network',
    variables: queryVariables,
  });

  const [fetchReportDownloadURI] = useLazyQuery<
    ApiEndpointUsageLogsReportDownloadUriQuery,
    ApiEndpointUsageLogsReportDownloadUriQueryVariables
  >(apiEndpointUsageLogsReportDownloadURIQuery, { fetchPolicy: 'network-only' });

  const handleExport = async () => {
    try {
      const { data } = await fetchReportDownloadURI({
        variables: {
          endpoint: endpoints.length > 0 ? endpoints.join(',') : undefined,
          implementationStatus: status || undefined,
          apiHealth: health || undefined,
        },
      });

      const uri = data?.apiEndpointUsageLogsReportDownloadURI;
      if (uri) {
        window.location.href = uri;
      }
    } catch (error) {
      console.error('Failed to generate export URL', error);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 130px)' }}>
      <Heading size="large">Visibility API</Heading>
      <p>
        This page lists the API usage in Transporeon Visibility. It includes Visibility API as well as other APIs
        (Sixfold legacy API, OVD, TTC).
      </p>
      <LoadingContainer
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        result={result as any}
        options={{
          showLoadingPlaceholder: ({ networkStatus, loading }) => {
            const isFetchingMore = networkStatus === NetworkStatus.fetchMore;
            return isFetchingMore ? false : loading;
          },
        }}
        mapData={({ data }) => ({
          data:
            getNodes(data.apiEndpointUsageLogs)?.map((log) => ({
              company_id: log.companyId,
              company_name: log.companyName,
              api_endpoint_usage_logs: [
                {
                  api_implemented_at: log.apiImplementedAt,
                  counters: log.counters,
                  endpoint_id: log.endpointId,
                  endpoint_name: log.endpointName,
                  first_call_at: log.firstCallAt,
                  last_call_at: log.lastCallAt,
                  integration_identifier: log.integrationIdentifier,
                  webhook_subscription: log.webhookSubscription,
                  implementation_status: log.implementationStatus,
                  api_health_status: log.apiHealthStatus,
                },
              ],
            })) ?? [],
          result,
        })}>
        {({ data, result: queryResult }) => (
          <>
            <div className="api-usage-container__filters">
              <EndpointDropdown className="api-usage-container__select" value={endpoints} onChange={onSetEndpoints} />
              <Select
                className="api-usage-container__select"
                label="Implementation status"
                labelPosition="block"
                onChange={(value) => onSetImplementationStatus(value?.value ?? '')}
                options={Object.values(ImplementationStatus).map((value) => ({
                  label: getImplementationStatusLabel(value),
                  value,
                }))}
                usePortal
                isClearable
                value={getStatusKey(status)}
              />
              <Select
                className="api-usage-container__select"
                label="API health"
                labelPosition="block"
                onChange={(value) => onSetApiHealth(value?.value ?? '')}
                options={Object.values(ApiHealth).map((value) => ({
                  label: getApiHealthLabel(value),
                  value,
                }))}
                usePortal
                isClearable
                value={getApiHealthKey(health)}
              />
              <Button
                className="api-usage-container__export-button"
                iconStart="small-download"
                onClick={() => void handleExport()}
                title="Export to Excel">
                <Text>Export</Text>
              </Button>
            </div>
            <InfiniteScroll
              className="api-usage-list"
              triggerOnWindow={false}
              loadMoreEntries={() =>
                loadMoreFromConnection(apiEndpointUsageLogs, queryResult, ['apiEndpointUsageLogs'])
              }>
              <ApiEndpointUsageLogsList
                data={data.map((log) => ({
                  company_id: log.company_id,
                  company_name: log.company_name,
                  api_endpoint_usage_logs_data: {
                    api_endpoint_usage_logs: log.api_endpoint_usage_logs,
                  },
                }))}
                isSingleCompany={false}
                hasActiveFilters={hasActiveFilters}
              />
            </InfiniteScroll>
          </>
        )}
      </LoadingContainer>
    </div>
  );
};
